import { useCallback, useEffect, useState } from "react";
import { useNavigate } from "react-router-dom";
import Box from "@mui/material/Box";
import CircularProgress from "@mui/material/CircularProgress";
import type { GridPaginationModel } from "@mui/x-data-grid-premium";
import { useSnackbar } from "../../../contexts/SnackbarContext";
import { useAssessmentPlanFilterOptions } from "../../../hooks/useAssessmentPlanFilterOptions";
import {
  buildConditionalPlansQuery,
  CONDITIONAL_PLANS_DEFAULT_PAGE_SIZE,
  fetchConditionalPlans,
  mapConditionalPlanToRuleRow,
  normalizeAutoTaggingPlanNameQuery,
  type ConditionalPlanRecord,
} from "../../../services/autoTaggingService";
import { hydrateReferenceFromPlan } from "../TaggingPlan/classificationPlanApiMapping";
import {
  AI_PLAN_ADD_ENHANCED_TAGGING_RULE_PATH,
  buildEditEnhancedTaggingRulePath,
} from "../constants";
import { formatConditionToNaturalLanguage } from "./formatConditionNaturalLanguage";
import EnhancedTaggingRuleFilterBar, {
  EMPTY_ENHANCED_TAGGING_RULE_FILTERS,
  type EnhancedTaggingRuleAppliedFilters,
} from "./EnhancedTaggingRuleFilterBar";
import EnhancedTaggingRuleTable from "./EnhancedTaggingRuleTable";
import type { EnhancedTaggingRuleRow } from "./types";

function withReferenceItems(
  row: EnhancedTaggingRuleRow,
  plan: ConditionalPlanRecord,
): EnhancedTaggingRuleRow {
  if (plan.reference_items.length === 0) return row;
  const reference = hydrateReferenceFromPlan({
    reference_items: plan.reference_items,
    sub_type: "",
  });
  return {
    ...row,
    objectSubType: reference.objectSubType,
    objectSubTypeName: reference.objectSubTypeName,
    referenceConceptCode: reference.referenceConceptCode,
    referenceConceptDisplayName: reference.referenceConceptDisplayName,
    referenceDimensions: reference.referenceDimensions,
  };
}

export default function EnhancedTaggingRuleTab() {
  const navigate = useNavigate();
  const { showMessage } = useSnackbar();
  const [searchText, setSearchText] = useState("");
  const [appliedFilters, setAppliedFilters] =
    useState<EnhancedTaggingRuleAppliedFilters>(
      EMPTY_ENHANCED_TAGGING_RULE_FILTERS,
    );
  const [rows, setRows] = useState<EnhancedTaggingRuleRow[]>([]);
  const [total, setTotal] = useState(0);
  const [loading, setLoading] = useState(true);
  const [hasLoaded, setHasLoaded] = useState(false);
  const [paginationModel, setPaginationModel] = useState<GridPaginationModel>({
    page: 0,
    pageSize: CONDITIONAL_PLANS_DEFAULT_PAGE_SIZE,
  });
  const { planTypes, planTypesLoading, conceptOptions } =
    useAssessmentPlanFilterOptions("", {
      configurableOnly: true,
    });

  // Top-bar search → `keyword` (fuzzy); single Assessment name filter → `name` (exact).
  const apiKeyword = normalizeAutoTaggingPlanNameQuery(searchText);
  const apiName =
    appliedFilters.assessmentNames.length === 1
      ? normalizeAutoTaggingPlanNameQuery(appliedFilters.assessmentNames[0] ?? "")
      : "";

  useEffect(() => {
    setPaginationModel((prev) =>
      prev.page === 0 ? prev : { ...prev, page: 0 },
    );
  }, [appliedFilters, searchText]);

  const loadRows = useCallback(() => {
    let mounted = true;
    setLoading(true);

    const query = buildConditionalPlansQuery({
      keyword: apiKeyword,
      name: apiName,
      page: paginationModel.page + 1,
      pageSize: paginationModel.pageSize,
    });

    fetchConditionalPlans(query)
      .then((result) => {
        if (!mounted) return;
        setRows(
          result.items.map((plan) =>
            withReferenceItems(
              mapConditionalPlanToRuleRow(plan, {
                conditionDescription: formatConditionToNaturalLanguage(
                  plan.condition,
                ),
                planTypes,
                concepts: conceptOptions,
              }),
              plan,
            ),
          ),
        );
        setTotal(result.total);
      })
      .catch(() => {
        if (!mounted) return;
        setRows([]);
        setTotal(0);
        showMessage("Failed to load Conditional Classifications.", "error");
      })
      .finally(() => {
        if (!mounted) return;
        setLoading(false);
        setHasLoaded(true);
      });

    return () => {
      mounted = false;
    };
  }, [
    apiKeyword,
    apiName,
    conceptOptions,
    paginationModel.page,
    paginationModel.pageSize,
    planTypes,
    showMessage,
  ]);

  useEffect(() => {
    const cleanup = loadRows();
    return cleanup;
  }, [loadRows]);

  const handleEdit = useCallback(
    (row: EnhancedTaggingRuleRow) => {
      navigate(buildEditEnhancedTaggingRulePath(row.id), {
        state: { rule: row },
      });
    },
    [navigate],
  );

  const showInitialLoading = (!hasLoaded && loading) || planTypesLoading;

  return (
    <Box
      sx={{
        display: "flex",
        flexDirection: "column",
        gap: "12px",
        flex: 1,
        minHeight: 0,
        mx: "16px",
        mb: "16px",
      }}
    >
      <EnhancedTaggingRuleFilterBar
        searchText={searchText}
        onSearchTextChange={setSearchText}
        appliedFilters={appliedFilters}
        onAppliedFiltersChange={setAppliedFilters}
      />
      {showInitialLoading ? (
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "center",
            minHeight: 320,
            border: "1px solid #E5E7EB",
            borderRadius: "8px",
            bgcolor: "#fff",
          }}
        >
          <CircularProgress size={28} />
        </Box>
      ) : (
        <EnhancedTaggingRuleTable
          rows={rows}
          rowCount={total}
          loading={loading}
          paginationModel={paginationModel}
          onPaginationModelChange={setPaginationModel}
          onAddNew={() => navigate(AI_PLAN_ADD_ENHANCED_TAGGING_RULE_PATH)}
          onEdit={handleEdit}
          onRuleUpdated={loadRows}
        />
      )}
    </Box>
  );
}
